<template>
    <div
        :class="[
            {
                'form-group': this.type !== 'hidden',
            },
            parentClass,
        ]"
    >
        <label
            v-if="label"
            :for="uniqueId"
            class="form-group-label d-flex align-items-center"
        >
            {{ label }}

            <span
                v-if="optional"
                class="d-inline-flex ml-2 text-small"
            >
                {{ $t('general.optional') }}
            </span>

            <ui-icon
                v-if="tooltip"
                icon="info"
                :content="tooltip"
                class="ml-2"
                v-tippy
            />
        </label>
        <div class="input-group">
            <slot name="icon" />

            <input
                v-model="inputValue"
                v-bind="$attrs"
                @input="handleInput"
                :id="uniqueId"
                :placeholder="placeholder || label"
                :type="type"
                class="form-control"
            >
            <div
                v-if="appendText || select"
                class="input-group-append"
            >
                <label
                    v-if="appendText"
                    :for="uniqueId"
                    class="input-group-text"
                >
                    {{ appendText }}
                </label>

                <div
                    v-if="select"
                    class="input-group-append"
                >
                    <ui-select
                        :name="select.name"
                        :options="select.options"
                        :value="select.value"
                    ></ui-select>
                </div>
            </div>
        </div>

        <slot />
    </div>
</template>

<script>
    import UiIcon from './icon.vue';
    import UiSelect from './select.vue';

    export default {
        name: 'UiInput',
        components: {
            UiIcon,
            UiSelect,
        },
        inheritAttrs: false,
        props: {
            appendText: {
                type: String,
                default: null,
            },
            disableCharacters: {
                type: Array,
                default: () => [],
            },
            helpText: {
                type: String,
                default: '',
            },
            label: {
                type: String,
                default: null,
            },
            optional: {
                type: Boolean,
                default: false,
            },
            parentClass: {
                type: String,
                default: '',
            },
            placeholder: {
                type: String,
                default: null,
            },
            select: {
                type: Object,
                default: () => {},
            },
            tooltip: {
                type: String,
                default: null,
            },
            type: {
                type: String,
                default: 'text',
            },
            value: {
                type: [String, Number],
                default: '',
            },
        },
        data() {
            return {
                inputValue: this.value,
            };
        },
        computed: {
            uniqueId() {
                return this._uid;
            },
        },
        methods: {
            handleInput() {
                if (this.disableCharacters.length) {
                    const regex = new RegExp(this.disableCharacters.join('|'), 'gi');

                    this.inputValue = this.inputValue.replace(regex, '');
                }

                this.$emit('input', this.inputValue);
            },
        },
        watch: {
            value(newVal) {
                this.inputValue = newVal;
            },
        },
    };
</script>

<style lang="scss">
    .form {
        &-group {
            position: relative;

            &:last-child {
                margin-bottom: 0;
            }

            .input-group .icon {
                position: absolute;
                top: 50%;
                left: 12px;
                z-index: 10;
                font-size: 22px;
                transform: translateY(-50%);

                + .form-control {
                    padding-left: 40px;
                }
            }
        }

        &-control {
            border-radius: $radius !important; // stylelint-disable-line

            .input-group & {
                @include placeholder {
                    opacity: 0.2;
                }
                height: 48px;
                transition: 0.3s;

                &:focus {
                    border-color: $primary;
                    box-shadow: none;
                }

                .active & {
                    @include placeholder {
                        color: transparent;
                    }
                }
            }
        }
    }

    .is-invalid {
        .invalid-feedback {
            display: block;
        }
    }

    .input-group-append .custom-select {
        background-color: $gray-200;
    }
</style>
